import React from 'react';
import useFileUpload, { ALLOWED_EXTENSIONS, LANGUAGE_OPTIONS, formatFileSize } from '../../hooks/useFileUpload';
import UploadProgress from './UploadProgress';
import StorageBar from '../Storage/StorageBar';
import './Upload.css';

function FileUpload() {
  const {
    user,
    fileInputRef,
    dragActive,
    files,
    selectedLanguage,
    setSelectedLanguage,
    isBatchActive,
    allDone,
    error,
    removeFile,
    updateFile,
    handleDrag,
    handleDrop,
    handleChange,
    handleUpload,
    handleComplete,
    handleReset,
  } = useFileUpload();

  // Show progress view once batch has started
  if (isBatchActive) {
    return (
      <UploadProgress
        files={files}
        allDone={allDone}
        onComplete={handleComplete}
        onReset={handleReset}
        onFileStatusChange={(fileId, status, errorMessage) => {
          updateFile(fileId, { status, error: errorMessage });
        }}
        isMobile={false}
      />
    );
  }

  const pendingFiles = files.filter(f => f.status === 'pending');
  const failedFiles = files.filter(f => f.status === 'failed');
  const totalSize = files.reduce((sum, f) => sum + f.file.size, 0);

  return (
    <div className="upload-container">
      <h1 className="upload-title">Upload Audio/Video</h1>
      <p className="upload-subtitle">
        Upload one or more files to transcribe at once. Supports MP3, WAV, MP4, and more.
      </p>

      <StorageBar storage={user?.storage} />

      {error && (
        <div className="alert alert-error">
          {error}
        </div>
      )}

      {/* File list */}
      {files.length > 0 && (
        <div className="upload-file-list">
          {files.map((fileEntry) => (
            <div key={fileEntry.id} className={`upload-file-list-item ${fileEntry.status === 'failed' ? 'file-invalid' : ''}`}>
              <span className="file-icon">
                {fileEntry.file.type?.startsWith('video/') ? '🎬' : '🎵'}
              </span>
              <div className="file-details">
                <span className="file-name">{fileEntry.file.name}</span>
                <span className="file-size">{formatFileSize(fileEntry.file.size)}</span>
                {fileEntry.error && (
                  <span className="file-error">{fileEntry.error}</span>
                )}
              </div>
              <button
                className="btn-remove"
                onClick={() => removeFile(fileEntry.id)}
                title="Remove file"
              >
                &times;
              </button>
            </div>
          ))}
          <div className="upload-file-list-summary">
            {pendingFiles.length > 0 && (
              <span>{pendingFiles.length} file{pendingFiles.length !== 1 ? 's' : ''} ready ({formatFileSize(pendingFiles.reduce((s, f) => s + f.file.size, 0))})</span>
            )}
            {failedFiles.length > 0 && (
              <span className="file-error"> &middot; {failedFiles.length} rejected</span>
            )}
          </div>
        </div>
      )}

      {/* Dropzone - always visible to allow adding more files */}
      <div
        className={`upload-dropzone ${dragActive ? 'active' : ''} ${files.length > 0 ? 'compact' : ''}`}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept={ALLOWED_EXTENSIONS.join(',')}
          onChange={handleChange}
          className="upload-input"
          multiple
        />

        <div className="upload-placeholder">
          {files.length === 0 ? (
            <>
              <div className="upload-icon">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                  <polyline points="17,8 12,3 7,8" />
                  <line x1="12" y1="3" x2="12" y2="15" />
                </svg>
              </div>
              <p className="upload-text">
                <strong>Click to upload</strong> or drag and drop
              </p>
              <p className="upload-hint">
                Select multiple audio or video files at once &mdash; up to 3GB each
              </p>
            </>
          ) : (
            <p className="upload-text">
              <strong>Add more files</strong> &mdash; click or drag and drop
            </p>
          )}
        </div>
      </div>

      {/* Options + submit */}
      {pendingFiles.length > 0 && (
        <div className="upload-options">
          <div className="form-group">
            <label className="form-label">Language</label>
            <select
              className="form-input"
              value={selectedLanguage}
              onChange={(e) => setSelectedLanguage(e.target.value)}
            >
              {LANGUAGE_OPTIONS.map((lang) => (
                <option key={lang.code} value={lang.code}>
                  {lang.label}
                </option>
              ))}
            </select>
            <p className="form-hint">Selecting the correct language may improve transcription accuracy</p>
          </div>
          <button
            className="btn btn-primary btn-lg upload-submit"
            onClick={handleUpload}
          >
            {pendingFiles.length === 1
              ? 'Start Transcription'
              : `Start Transcription (${pendingFiles.length} files)`}
          </button>
        </div>
      )}
    </div>
  );
}

export default FileUpload;
